import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles, UserRound, Leaf, Globe2, Cpu, Stethoscope, GraduationCap,
  Briefcase, Palette, FlaskConical, Dumbbell, Wheat, MapPin, Camera,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { localizeMessage } from '../utils/toast.js';
import "./PlayerInterest.css";

const USERNAME_REGEX = /^[a-z0-9_.]{3,20}$/;

function PlayerInterest() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUserProfile, updateUserInterests, checkUsernameAvailable, loading } = useAuth();
  const [step, setStep] = useState("details");
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [usernameStatus, setUsernameStatus] = useState("idle");
  const usernameCheckRef = useRef(0);

  useEffect(() => {
    if (!loading && !user) navigate("/registration");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      setUsername(user.username || "");
      setName(user.name || "");
      setSurname(user.surname || "");
      setDateOfBirth(user.dateOfBirth ? String(user.dateOfBirth).slice(0, 10) : "");
    }
  }, [user]);

  useEffect(() => {
    const candidate = username.trim().toLowerCase();
    if (!candidate || candidate === (user?.username || "").toLowerCase()) {
      setUsernameStatus("idle");
      return;
    }
    if (!USERNAME_REGEX.test(candidate)) {
      setUsernameStatus("invalid");
      return;
    }
    setUsernameStatus("checking");
    const requestId = ++usernameCheckRef.current;
    const timer = setTimeout(async () => {
      if (!checkUsernameAvailable) return;
      const res = await checkUsernameAvailable(candidate);
      if (usernameCheckRef.current !== requestId) return;
      setUsernameStatus(res?.success && res.available ? "available" : "taken");
    }, 450);
    return () => clearTimeout(timer);
  }, [username, user, checkUsernameAvailable]);

  // IDs and colors match FB_SECTORS in OrgProgramSubmit.jsx exactly (same
  // orgProgramSubmit.sectorLabels.* translations too) — this is also the
  // vocabulary Program.sectors is tagged with, and what computeUserInterestProfile
  // on the backend scores candidate programs against. Picking from any other list
  // (or sending translated display text instead of these ids) means the selection
  // here never actually overlaps a program's tags, so it silently stops
  // contributing to recommendations/AI matching despite looking like it works.
  const interests = [
    { id: 'ecology',       name: t('orgProgramSubmit.sectorLabels.ecology'),       Icon: Leaf,          color: '#16a34a' },
    { id: 'international', name: t('orgProgramSubmit.sectorLabels.international'), Icon: Globe2,        color: '#2563eb' },
    { id: 'technology',    name: t('orgProgramSubmit.sectorLabels.technology'),    Icon: Cpu,           color: '#d97706' },
    { id: 'medical',       name: t('orgProgramSubmit.sectorLabels.medical'),       Icon: Stethoscope,   color: '#dc2626' },
    { id: 'education',     name: t('orgProgramSubmit.sectorLabels.education'),     Icon: GraduationCap, color: '#7c3aed' },
    { id: 'business',      name: t('orgProgramSubmit.sectorLabels.business'),      Icon: Briefcase,     color: '#1d4ed8' },
    { id: 'arts',          name: t('orgProgramSubmit.sectorLabels.arts'),          Icon: Palette,       color: '#be185d' },
    { id: 'science',       name: t('orgProgramSubmit.sectorLabels.science'),       Icon: FlaskConical,  color: '#0891b2' },
    { id: 'sports',        name: t('orgProgramSubmit.sectorLabels.sports'),        Icon: Dumbbell,      color: '#ea580c' },
    { id: 'agriculture',   name: t('orgProgramSubmit.sectorLabels.agriculture'),   Icon: Wheat,         color: '#65a30d' },
    { id: 'local',         name: t('orgProgramSubmit.sectorLabels.local'),         Icon: MapPin,        color: '#8b5cf6' },
    { id: 'media',         name: t('orgProgramSubmit.sectorLabels.media'),         Icon: Camera,        color: '#db2777' },
  ];

  const toggleInterest = (id) => {
    setError("");
    if (selectedInterests.includes(id)) {
      setSelectedInterests(selectedInterests.filter(i => i !== id));
    } else {
      if (selectedInterests.length < 5) {
        setSelectedInterests([...selectedInterests, id]);
      } else {
        setError(t('playerInterest.maxInterests'));
      }
    }
  };

  const setOnboardingFlags = () => {
    localStorage.setItem("showOnboarding", "true");
    localStorage.setItem("completedPlayerInterests", "true");
  };

  const handleDetailsNext = async () => {
    setError("");
    const trimmedUsername = username.trim().toLowerCase();
    const trimmedName = name.trim();
    if (!trimmedUsername || !USERNAME_REGEX.test(trimmedUsername)) {
      setError(t('playerInterest.invalidUsername'));
      return;
    }
    if (usernameStatus === "taken") { setError(t('playerInterest.usernameTaken')); return; }
    if (usernameStatus === "checking") { setError(t('playerInterest.usernameChecking')); return; }
    if (!trimmedName) { setError(t('playerInterest.nameRequired')); return; }

    setIsSubmitting(true);
    try {
      if (updateUserProfile) {
        const result = await updateUserProfile({
          username: trimmedUsername,
          name: trimmedName,
          surname: surname.trim(),
          dateOfBirth: dateOfBirth || undefined,
        });
        if (!result || !result.success) {
          setError(result?.error || t('playerInterest.failedSave'));
          return;
        }
      }
      setStep("interests");
    } catch {
      setError(t('playerInterest.errorSaving'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (selectedInterests.length === 0) { setError(t('playerInterest.selectAtLeastOne')); return; }
    setIsSubmitting(true); setError("");
    try {
      // Send the canonical ids (e.g. "ecology"), not the translated display names —
      // see the comment above `interests` for why that distinction matters here.
      const ids = interests.filter(i => selectedInterests.includes(i.id)).map(i => i.id);
      if (updateUserInterests) {
        const result = await updateUserInterests(ids);
        if (result && result.success) { setOnboardingFlags(); navigate("/feed"); }
        else setError(result?.error || t('playerInterest.failedSave'));
      } else { setOnboardingFlags(); navigate("/feed"); }
    } catch { setError(t('playerInterest.errorSaving')); }
    finally { setIsSubmitting(false); }
  };

  const handleSkip = () => { setOnboardingFlags(); navigate("/feed"); };
  const handleSkipDetails = () => { setError(""); setStep("interests"); };

  if (loading) {
    return (
      <div className="pi-loading-container">
        <div className="pi-loading-spinner" />
        <p style={{ color: "var(--ink-600)" }}>{t('playerInterest.loading')}</p>
      </div>
    );
  }

  if (step === "details") {
    return (
      <div className="pi-page">
        <div className="pi-container">
          <div className="pi-header">
            <div className="pi-header-icon">
              <UserRound size={22} strokeWidth={2.2} />
            </div>
            <h2>{t('playerInterest.detailsTitle')}</h2>
            <p className="pi-subtitle">{t('playerInterest.detailsSubtitle')}</p>
          </div>

          {error && <div className="pi-error">{localizeMessage(error)}</div>}

          <div className="pi-form">
            <label className="pi-field">
              <span>{t('playerInterest.usernameLabel')}</span>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder={t('playerInterest.usernamePlaceholder')}
                maxLength={20}
                autoCapitalize="none"
              />
              {usernameStatus === "checking" && <small className="pi-hint">{t('playerInterest.usernameChecking')}</small>}
              {usernameStatus === "available" && <small className="pi-hint pi-hint-ok">{t('playerInterest.usernameAvailable')}</small>}
              {usernameStatus === "taken" && <small className="pi-hint pi-hint-error">{t('playerInterest.usernameTaken')}</small>}
              {usernameStatus === "invalid" && <small className="pi-hint pi-hint-error">{t('playerInterest.invalidUsername')}</small>}
            </label>

            <div className="pi-field-row">
              <label className="pi-field">
                <span>{t('playerInterest.nameLabel')}</span>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t('playerInterest.nameLabel')} />
              </label>
              <label className="pi-field">
                <span>{t('playerInterest.surnameLabel')}</span>
                <input type="text" value={surname} onChange={e => setSurname(e.target.value)} placeholder={t('playerInterest.surnameLabel')} />
              </label>
            </div>

            <label className="pi-field">
              <span>{t('playerInterest.dobLabel')}</span>
              <input type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
            </label>
          </div>

          <div className="pi-actions">
            <button className="pi-skip-btn" onClick={handleSkipDetails} disabled={isSubmitting}>
              {t('playerInterest.skipForNow')}
            </button>
            <button className="pi-next-btn" onClick={handleDetailsNext} disabled={isSubmitting}>
              {isSubmitting ? (<><span className="pi-spinner" />{t('playerInterest.saving')}</>) : t('playerInterest.continueBtn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pi-page">
      <div className="pi-container">
        <div className="pi-header">
          <div className="pi-header-icon">
            <Sparkles size={22} strokeWidth={2.2} />
          </div>
          <h2>{t('playerInterest.title')}</h2>
          <p className="pi-subtitle">{t('playerInterest.subtitle')}</p>
        </div>

        {error && <div className="pi-error">{localizeMessage(error)}</div>}

        <div className="pi-grid">
          {interests.map((interest) => {
            const selected = selectedInterests.includes(interest.id);
            return (
              <div
                key={interest.id}
                onClick={() => toggleInterest(interest.id)}
                className={`pi-card${selected ? ' selected' : ''}`}
              >
                {selected && <div className="pi-checkmark">✓</div>}
                <div className="pi-image" style={{ background: `${interest.color}22` }}>
                  <interest.Icon size={28} color={interest.color} strokeWidth={2} />
                </div>
                <div className="pi-content">
                  <h3>{interest.name}</h3>
                </div>
              </div>
            );
          })}
        </div>

        {selectedInterests.length > 0 && (
          <div className="pi-selected-info">
            <p>{t('playerInterest.selected')}: <span className="pi-count">{selectedInterests.length}</span> / 5</p>
            <div className="pi-selected-list">
              {selectedInterests.map(id => {
                const interest = interests.find(i => i.id === id);
                return interest ? (
                  <span key={id} className="pi-selected-tag">
                    {interest.name}
                    <button className="pi-remove-tag" onClick={e => { e.stopPropagation(); toggleInterest(id); }}>×</button>
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}

        <div className="pi-actions">
          <button className="pi-skip-btn" onClick={handleSkip} disabled={isSubmitting}>
            {t('playerInterest.skipForNow')}
          </button>
          <button className="pi-next-btn" onClick={handleNext} disabled={selectedInterests.length === 0 || isSubmitting}>
            {isSubmitting ? (<><span className="pi-spinner" />{t('playerInterest.saving')}</>) : t('playerInterest.continueBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlayerInterest;
